import { ErrorAlert, LoadingAlert, WarningAlert } from '@/components';
import Table from '@/components/molecules/Table/Table';
import WalletListItem from '@/components/organisms/WalletListItem/WalletListItem';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { logError, logInfo } from '@/utils/logger';
import { WALLET } from '@/utils/strings';
import React, { useMemo } from 'react';
interface WalletsListProps {
  onSelectWallet?: (wallet: { blockchain?: string; address: string }) => void;
  showWalletAddressCopyButton?: boolean;
  showWalletAddress?: boolean;
  shouldSelectWallet?: boolean;
  showFullWalletAddress?: boolean;
}
const WalletsList: React.FC<WalletsListProps> = ({
  onSelectWallet,
  showWalletAddressCopyButton = true,
  showWalletAddress = true,
  shouldSelectWallet = true,
  showFullWalletAddress = false,
}) => {
  const { wallets, isInitialLoading, error, isUserAuthorized } =
    useWalletsContext();
  const renderContent = useMemo(() => {
    if (isInitialLoading) {
      logInfo('Initial loading in progress for wallets', 'WalletsList');
      return (
        <LoadingAlert
          title={WALLET.LOADING_TITLE}
          description={WALLET.LOADING_DESCRIPTION}
        />
      );
    }
    if (!isUserAuthorized) {
      logError('User is not authorized to use Sliver', 'WalletsList');
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    if (error) {
      logError('Error loading wallets.', 'WalletsList', { error });
      return (
        <ErrorAlert
          title={WALLET.GENERIC_ERROR_LOADING_TITLE}
          description={WALLET.GENERIC_ERROR_LOADING_DESCRIPTION}
        />
      );
    }
    if (!wallets || wallets.length === 0) {
      return (
        <WarningAlert
          title={WALLET.WALLET_NOT_FOUND_TITLE}
          description={WALLET.WALLET_NOT_FOUND_DESCRIPTION}
        />
      );
    }
    const handleRowClick = (
      _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      rowIndex: number,
    ) => {
      if (shouldSelectWallet && onSelectWallet) {
        const selectedWallet = wallets[rowIndex];
        onSelectWallet(selectedWallet);
      }
    };
    const rows = wallets.map((wallet) => ({
      cells: [
        {
          mainText: (
            <WalletListItem
              wallet={wallet}
              showWalletAddressCopyButton={showWalletAddressCopyButton}
              showWalletAddress={showWalletAddress}
              showFullWalletAddress={showFullWalletAddress}
            />
          ),
        },
      ],
    }));
    return (
      <Table
        columnWidths={[100]}
        rows={rows}
        onClickRow={shouldSelectWallet ? handleRowClick : undefined}
      />
    );
  }, [
    isInitialLoading,
    isUserAuthorized,
    error,
    wallets,
    shouldSelectWallet,
    onSelectWallet,
    showWalletAddressCopyButton,
    showWalletAddress,
    showFullWalletAddress,
  ]);
  return renderContent;
};
export default WalletsList;
